import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { api, fmt } from "@/lib/api";
import { LogOut, Shield, Radio } from "lucide-react";

const links = [
  { to: "/dashboard", label: "Dashboard", id: "nav-dashboard" },
  { to: "/rewards", label: "Rewards", id: "nav-rewards" },
  { to: "/quests", label: "Quests", id: "nav-quests" },
  { to: "/achievements", label: "Achievements", id: "nav-achievements" },
  { to: "/leaderboard", label: "Leaderboard", id: "nav-leaderboard" },
  { to: "/giveaway", label: "Giveaway", id: "nav-giveaway" },
];

export const NavBar = () => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [watchLive, setWatchLive] = useState(0);

  useEffect(() => {
    if (!user) return;
    const check = () =>
      api.get("/watch/status").then(({ data }) => setWatchLive(data.live)).catch(() => {});
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, [user]);

  return (
    <header className="glass sticky top-0 z-50">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/dashboard" data-testid="nav-brand-link">
          <Brand />
        </Link>
        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${
                loc.pathname === l.to
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {(watchLive > 0 || loc.pathname === "/watch") && (
            <Link
              to="/watch"
              data-testid="nav-watch"
              className={`flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${
                loc.pathname === "/watch"
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Radio className="h-3 w-3 text-red-500" /> Watch
            </Link>
          )}
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="ml-2 flex items-center gap-2 border border-primary/40 px-4 py-2 text-xs uppercase tracking-[0.18em] text-primary hover:bg-primary/10"
            >
              <Shield className="h-3.5 w-3.5" /> Admin
            </Link>
          )}
        </nav>
        <div className="flex items-center gap-4">
          {user && (
            <div className="text-right" data-testid="nav-points">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Points</div>
              <div className="mono text-lg font-semibold text-primary">{fmt(user.points_balance)}</div>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            data-testid="logout-btn"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};
